import { AccountDomainStatus } from '@/client/accounts/types/account-domain.type';
import {
  useAccountDomains,
  useAddDomain,
  useSuppressedDomains,
} from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { DomainInput } from '@/ui';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DomainCard } from './DomainCard';

const StyledDomainContainer = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--big-padding);
  overflow: hidden;
  margin-top: var(--medium-big-padding);
`;

const StyledDomainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

type DomainVerificationProps = {
  accountId: string;
};

export const DomainVerification: React.FC<DomainVerificationProps> = ({
  accountId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { domains, isLoading } = useAccountDomains(accountId);
  const { addDomain, isLoading: isAdding } = useAddDomain(accountId);
  const { suppressedDomains } = useSuppressedDomains();

  const [newDomain, setNewDomain] = useState('');
  const [inlineMessage, setInlineMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);

  const handleAddDomain = async () => {
    const domainValue = newDomain.toLowerCase().trim();

    if (!domainValue) return;

    setInlineMessage(null);

    const isSuppressed = suppressedDomains?.result?.some(
      (d: { domain: string }) => d.domain === domainValue,
    );

    if (isSuppressed) {
      setInlineMessage({
        text: t('account.domains.error.personal'),
        variant: 'error',
      });
      return;
    }

    const existing = domains?.find((d) => d.domain === domainValue);
    if (existing) {
      if (existing.status === AccountDomainStatus.VERIFIED) {
        setInlineMessage({
          text: t('account.domains.error.alreadyVerified'),
          variant: 'success',
        });
      } else {
        setInlineMessage({
          text: t('account.domains.error.alreadyExists'),
          variant: 'error',
        });
      }
      return;
    }

    try {
      await addDomain({ domain: domainValue });
      setNewDomain('');
      setInlineMessage(null);
      toast?.success(t('toast.success'), t('account.domains.added'));
    } catch {
      toast?.error(t('toast.error'), t('account.domains.addFailed'));
    }
  };

  if (isLoading) {
    return <ProgressSpinner style={{ width: '40px', height: '40px' }} />;
  }

  return (
    <StyledDomainContainer>
      <StyledDomainList>
        {domains?.map((domain) => (
          <DomainCard key={domain.id} domain={domain} accountId={accountId} />
        ))}

        <DomainInput
          value={newDomain}
          onChange={(value) => {
            setNewDomain(value);
            if (inlineMessage) setInlineMessage(null);
          }}
          onAdd={handleAddDomain}
          placeholder={t('account.domains.addPlaceholder')}
          message={inlineMessage}
          disabled={isAdding}
        />
      </StyledDomainList>
    </StyledDomainContainer>
  );
};
